// Pure logic of the Agent Routeur page (RouterAgent.js): coverage of the
// routing instructions, « À vérifier », the tiles from
// GET /api/agent-router/logs/stats and the request shapes (unchanged).
//
// How the backend picks the instructions (Backend
// app/services/agent_router_service.get_router_instructions): only ACTIVE
// documents count — the brand's (mailbox-scoped first), else « Toutes les
// marques » (mailbox-scoped first). A brand whose own instructions are
// disabled therefore falls back on the general ones.

export const PERIODS = [7, 30, 90, 365];
export const LOGS_PAGE_SIZE = 20;

const sameBrand = (a, b) => String(a || '') === String(b || '');
export const brandIdOf = (brand) => String(brand?.id || brand?._id || '');

// One row per scope: « Toutes les marques » then each brand. `state`:
// active (own active instructions) | fallback (own disabled, the general
// ones apply) | covered (none of its own, the general ones apply) |
// off (nothing active applies: its e-mails are not routed).
export const routerCoverage = (instructions, brands) => {
  const list = Array.isArray(instructions) ? instructions : [];
  const globalActive = list.some((doc) => !doc.brand_id && doc.is_active);
  const scopes = [{ brandId: '', brand: null }, ...(brands || []).map((brand) => ({ brandId: brandIdOf(brand), brand }))];
  return scopes.map(({ brandId, brand }) => {
    const own = list.filter((doc) => sameBrand(doc.brand_id, brandId));
    const ownActive = own.some((doc) => doc.is_active);
    let state;
    if (ownActive) state = 'active';
    else if (brandId && globalActive) state = own.length ? 'fallback' : 'covered';
    else state = 'off';
    return { brandId, brand, isGlobal: !brandId, instructions: own, state };
  });
};

// « À vérifier »: brands left out while routing is in use, topics the
// router cannot choose well (it reads their description).
export const routerAttention = ({ instructions, topics, brands }) => {
  const items = [];
  const list = Array.isArray(instructions) ? instructions : [];
  if (list.some((doc) => doc.is_active)) {
    const off = routerCoverage(list, brands).filter((row) => !row.isGlobal && row.state === 'off');
    if (off.length) {
      items.push({ key: 'unrouted', kind: 'unrouted', tone: 'warning', count: off.length, names: off.map((row) => row.brand.name), brandId: off.length === 1 ? off[0].brandId : null });
    }
  }
  const missing = (topics || []).filter((topic) => !String(topic.description || '').trim());
  if (missing.length) {
    items.push({ key: 'nodesc', kind: 'nodesc', tone: 'warning', count: missing.length, names: missing.map((topic) => topic.name), topic: missing.length === 1 ? missing[0] : null });
  }
  return items;
};

export const routerKpis = (stats) => {
  if (!stats || typeof stats.total_routings !== 'number') return null;
  const total = stats.total_routings;
  const spam = stats.spam_count || 0;
  return {
    total,
    spam,
    routed: Math.max(0, total - spam),
    spamRate: total > 0 ? (typeof stats.spam_rate === 'number' ? stats.spam_rate : spam / total) : null,
    routedRate: total > 0 ? Math.max(0, total - spam) / total : null,
  };
};

// Breakdown rows (non-spam decisions only, as the endpoint counts them):
// share of the routed e-mails, biggest first. « Unknown » = no topic / agent.
export const breakdownRows = (breakdown) => {
  const entries = Object.entries(breakdown || {}).filter(([, count]) => typeof count === 'number');
  const total = entries.reduce((sum, [, count]) => sum + count, 0);
  return entries
    .sort((a, b) => b[1] - a[1])
    .map(([label, count]) => ({ label, unknown: label === 'Unknown', count, share: total > 0 ? count / total : 0 }));
};

export const decisionOf = (log) => {
  if (log.is_spam) return 'spam';
  if (log.redirected_to) return 'redirect';
  return 'routed';
};

// ---- request shapes (unchanged from the previous page) --------------------

export const instructionFormFrom = (doc, presetBrandId = '') => ({
  brand_id: doc ? (doc.brand_id || '') : presetBrandId,
  is_active: doc ? doc.is_active : true,
  instructions: doc?.instructions || '',
  apply_to_emails: doc?.apply_to_emails || [],
  redirect_emails: doc?.redirect_emails || [],
});

// PUT /api/agent-router/instructions/{id} body; POST adds brand_id.
export const instructionPayload = (form) => ({
  is_active: form.is_active,
  instructions: form.instructions,
  apply_to_emails: form.apply_to_emails.length > 0 ? form.apply_to_emails : null,
  redirect_emails: form.redirect_emails.length > 0 ? form.redirect_emails : null,
});

export const instructionCreatePayload = (form) => ({ ...instructionPayload(form), brand_id: form.brand_id || null });

// PUT /api/logistics/topics-config body (TopicsConfigRequest).
export const topicsConfigPayload = (config) => ({
  classification_enabled: config.classification_enabled,
  disabled_topics: config.disabled_topics || [],
  custom_topics: (config.custom_topics || []).map((c) => ({
    slug: c.slug || null, name: c.name, description: c.description || '',
  })),
});

export const toggleDefaultTopic = (config, slug) => {
  const disabled = new Set(config.disabled_topics || []);
  if (disabled.has(slug)) disabled.delete(slug); else disabled.add(slug);
  return { ...config, disabled_topics: [...disabled] };
};

// Query of /logs (page) and /logs/export (no page): same keys, same order.
export const routerLogQuery = ({ page, limit, brand, filters }) => {
  const params = new URLSearchParams(page ? { page, limit } : undefined);
  if (brand) params.append('brand_id', brand);
  if (filters.topic) params.append('topic', filters.topic);
  if (filters.is_spam !== '') params.append('is_spam', filters.is_spam);
  if (filters.source) params.append('source', filters.source);
  return params.toString();
};

export const routerStatsQuery = ({ days, brand }) => {
  const params = new URLSearchParams({ days });
  if (brand) params.append('brand_id', brand);
  return params.toString();
};

// Adds a typed redirect address (Enter or comma), deduplicated.
export const addRedirect = (list, raw) => {
  const value = String(raw || '').trim().replace(/,$/, '');
  if (!value || list.includes(value)) return list;
  return [...list, value];
};
